/**
 * Route Renderer Component
 * ============================================================================
 *
 * Purpose:
 * --------
 * Renders routes from declarative configuration.
 * Bridges the gap between route config (data) and React Router (components).
 *
 * This component handles:
 * - Converting route configuration to React Router routes
 * - Wrapping routes in appropriate layouts
 * - Applying protection and permissions
 * - Handling lazy-loaded components with Suspense
 *
 * Design Principles:
 * ------------------
 * - Declarative: Consumes pure data configuration
 * - Automatic: Handles layout, protection, and loading automatically
 * - Type-safe: Full TypeScript coverage
 * - Flexible: Supports nested routes and custom error boundaries
 *
 * Non-goals:
 * ----------
 * - Route definition (handled by route config)
 * - Navigation logic (handled by navigation service)
 * - Permission checking (delegated to ProtectedRoute)
 *
 * Usage:
 * ------
 * ```typescript
 * // In your app root
 * import { appRouteConfig } from 'navigation/routes/app-routes.config';
 *
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <RouteRenderer config={appRouteConfig} />
 *     </BrowserRouter>
 *   );
 * }
 * ```
 * ============================================================================
 */

import { Suspense, type ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { usePortalRedirect } from '@/core/auth/hooks/use-portal-redirect';
import ProtectedRoute from './protected-route';
import { LAYOUT_TYPES } from 'navigation/constants';
import type {
  AppRouteConfig,
  RouteConfig,
  RouteGroup,
  RouteRendererProps,
} from 'navigation/types';

// Import layout components
import { AppLayout } from 'layout/app';
import { AdminLayout } from 'layout/admin';
import AuthLayout from 'layout/wrapper/auth-layout';
import PublicLayout from 'layout/wrapper/public-layout';

/**
 * Default loading component
 *
 * Shown while lazy components are loading.
 * Can be replaced with custom loading component via props.
 */
const DefaultLoadingFallback = (): ReactElement => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

/**
 * Layout wrapper component
 *
 * Wraps route components in appropriate layout based on route config.
 */
const LayoutWrapper = ({
  children,
  layoutType,
}: {
  children: ReactElement;
  layoutType: string;
}): ReactElement => {
  switch (layoutType) {
    case LAYOUT_TYPES.DASHBOARD:
      return <AppLayout>{children}</AppLayout>;

    case LAYOUT_TYPES.ADMIN:
      return <AdminLayout>{children}</AdminLayout>;

    case LAYOUT_TYPES.AUTH:
      return <AuthLayout>{children}</AuthLayout>;

    case LAYOUT_TYPES.PUBLIC:
      return <PublicLayout>{children}</PublicLayout>;

    case LAYOUT_TYPES.FULLSCREEN:
      // Fullscreen layout - no wrapper
      return <div className="min-h-screen">{children}</div>;

    case LAYOUT_TYPES.NONE:
    default:
      // No layout wrapper
      return children;
  }
};

/**
 * Route element renderer
 *
 * Handles protection, layout, and suspense for individual routes.
 */
const renderRouteElement = (
  routeConfig: RouteConfig,
  groupPermission?: RouteGroup['permission'],
  groupLayout?: RouteGroup['layout'],
  defaultLoadingComponent?: ReactElement
): ReactElement => {
  const Component = routeConfig.component;
  const LoadingComponent =
    routeConfig.loadingComponent ?? defaultLoadingComponent ?? (
      <DefaultLoadingFallback />
    );

  // Merge group-level and route-level permissions
  const permission = {
    ...groupPermission,
    ...routeConfig.permission,
  };

  // Determine layout (route-level overrides group-level)
  const layout = routeConfig.layout ?? groupLayout ?? LAYOUT_TYPES.NONE;

  // Handle redirect routes
  // if (routeConfig.redirect) {
  //   return (
  //     <Navigate
  //       to={routeConfig.redirect.to}
  //       replace={routeConfig.redirect.replace}
  //     />
  //   );
  // }

  // Wrap component in Suspense for lazy loading
  let element = (
    <Suspense fallback={LoadingComponent}>
      <Component />
    </Suspense>
  );

  // Wrap in layout
  element = <LayoutWrapper layoutType={layout}>{element}</LayoutWrapper>;

  // Wrap in protection if route requires it
  // if (permission?.requiresAuth || permission?.allowedRoles) {
  //   element = (
  //     <ProtectedRoute
  //       requiresAuth={permission.requiresAuth}
  //       allowedRoles={permission.allowedRoles}
  //       customCheck={permission.customCheck}
  //       loadingComponent={LoadingComponent}
  //     >
  //       {element}
  //     </ProtectedRoute>
  //   );
  // }

  return element;
};

/**
 * Route Renderer Component
 *
 * Main component that renders all application routes from configuration.
 */
const RouteRenderer = ({
  config,
  defaultLoadingComponent,
}: RouteRendererProps): ReactElement => {
  // Enforce subdomain-based portal routing (admin.bimanova.com ↔ app.bimanova.com)
  usePortalRedirect();
  /**
   * Render route element
   *
   * Creates React Router <Route> element from route config.
   */
  const renderRoute = (
    route: RouteConfig,
    groupPermission?: RouteGroup['permission'],
    groupLayout?: RouteGroup['layout']
  ): ReactElement => {
    const element = renderRouteElement(
      route,
      groupPermission,
      groupLayout,
      defaultLoadingComponent
    );

    return (
      <Route
        key={route.path}
        path={route.path}
        index={route.index}
        element={element}
        errorElement={
          route.errorBoundary ? <route.errorBoundary /> : undefined
        }
      >
        {/* Render nested routes if any */}
        {route.children?.map((childRoute) =>
          renderRoute(childRoute, groupPermission, groupLayout)
        )}
      </Route>
    );
  };

  /**
   * Render route group
   *
   * Creates routes for an entire route group.
   */
  const renderRouteGroup = (group: RouteGroup): ReactElement[] => {
    return group.routes.map((route) =>
      renderRoute(route, group.permission, group.layout)
    );
  };

  return (
    <Routes>
      {/* Render all route groups */}
      {config.groups.map((group) => renderRouteGroup(group))}

      {/* Render fallback (404) route last */}
      <Route
        path={config.fallback.path}
        element={renderRouteElement(
          config.fallback,
          undefined,
          undefined,
          defaultLoadingComponent
        )}
      />
    </Routes>
  );
};

RouteRenderer.displayName = 'RouteRenderer';

export default RouteRenderer;
